import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import DevkitCard from './DevkitCard';
import DevkitListView from './DevkitListView';
import DashboardHeader from './Header';
import BatchUpdatePanel from './BatchUpdatePanel';
import BatchUpdateSidebar from './BatchUpdateSidebar';
import { useUser } from '../contexts/UserContext';
import { getCsrfToken, getRefreshToken } from '../utils/auth';
import { fetchWithAutoRefresh as fetchWithRefresh } from '../utils/fetchWithAutoRefresh';
import Footer from './Footer';
import DeviceSearchBar, { matchesDeviceSearch } from './DeviceSearchBar';

const PINNED_DEVKITS_STORAGE_KEY = 'pinnedDevkits';

const Dashboard = ({ setUser, company }) => {
	const { admin, setAdmin, setCompany } = useUser()
	const [currentView, setCurrentView] = useState(() => localStorage.getItem('viewMode') ?? 'grid');
	const [deviceSearch, setDeviceSearch] = useState('');

	const setCurrentViewPersisted = (view) => {
		localStorage.setItem('viewMode', view);
		setCurrentView(view);
	};

	const setOnlyShowOnlinePersisted = (val) => {
		localStorage.setItem('onlyShowOnline', val ? 'true' : 'false');
		setOnlyShowOnline(val);
	};

	const setSelectedCompanyPersisted = (val) => {
		if (val === null || val === undefined) {
			localStorage.removeItem('selectedCompany');
		} else {
			localStorage.setItem('selectedCompany', val);
		}
		setSelectedCompany(val);
	};

	const setSelectedDeviceTypesPersisted = (next) => {
		const arr = Array.isArray(next) ? next : [];
		localStorage.setItem('deviceTypeFilter', JSON.stringify(arr));
		setSelectedDeviceTypes(arr);
	};

	const getDeviceType = (d) => {
		if (d.is_mac_mini) return 'macmini';
		return Number(d.devkit_id) >= 2000 ? 'apollocore' : 'proteuscore';
	};

	const matchesDeviceType = (d, selectedTypes) => {
		if (!selectedTypes || selectedTypes.length === 0) return true;
		return selectedTypes.includes(getDeviceType(d));
	};
	const [devices, setDevices] = useState([]);
	const [pinnedDevkits, setPinnedDevkits] = useState(() => {
		try {
			const saved = window.localStorage.getItem(PINNED_DEVKITS_STORAGE_KEY);
			const parsed = saved ? JSON.parse(saved) : [];
			return Array.isArray(parsed) ? parsed : [];
		} catch {
			return [];
		}
	});
	const [error, setError] = useState(null);
	const [onlyShowOnline, setOnlyShowOnline] = useState(() => localStorage.getItem('onlyShowOnline') === 'true');
	const [companyList, setCompanyList] = useState([]);
	const [selectedCompany, setSelectedCompany] = useState(() => localStorage.getItem('selectedCompany') ?? null);
	const [selectedDeviceTypes, setSelectedDeviceTypes] = useState(() => {
		const raw = localStorage.getItem('deviceTypeFilter');
		if (!raw) return [];
		try {
			const parsed = JSON.parse(raw);
			if (Array.isArray(parsed)) return parsed;
		} catch {
			// fall through to legacy string handling
		}
		if (raw === 'all' || raw === '') return [];
		return [raw];
	});
	const [selectedDevkits, setSelectedDevkits] = useState([]);
	const [loading, setLoading] = useState(true);
	const [sidebarOpen, setSidebarOpen] = useState(false);

	const [tsStatus, setTsStatus] = useState({
		loading: false,
		indicator: null,
		description: null,
		components: [],
		error: null,
		checkedAt: null
	});

	const lastTsCheckRef = useRef(0);

	const checkTailscaleStatus = async (force = false) => {
		const now = Date.now();
		if (!force && now - lastTsCheckRef.current < 2 * 60 * 1000) return;
		lastTsCheckRef.current = now;

		setTsStatus(s => ({ ...s, loading: true, error: null }));
		try {
			const res = await fetch("https://status.tailscale.com/api/v2/summary.json");
			if (!res.ok) throw new Error(`HTTP ${res.status}`);
			const data = await res.json();

			setTsStatus({
				loading: false,
				indicator: data?.status?.indicator ?? 'none',
				description: data?.status?.description ?? null,
				components: Array.isArray(data?.components)
					? data.components.map(c => ({ id: c.id, name: c.name, status: c.status }))
					: [],
				error: null,
				checkedAt: new Date()
			});
		} catch (e) {
			setTsStatus({
				loading: false,
				indicator: 'unknown',
				description: null,
				components: [],
				error: e?.message || "Failed to query Tailscale status",
				checkedAt: new Date()
			});
		}
	};

	useEffect(() => {
		const keepAlive = async () => {
			try {
				const csrf = getRefreshToken()
				await fetch(`/api/token/refresh`, {
					method: 'POST',
					credentials: 'include',
					headers: { 'X-CSRF-TOKEN': csrf },
				});
				// console.log('slid refresh');
			} catch (_) { }
		};
		keepAlive();
		const id = setInterval(keepAlive, 10 * 60 * 1000);
		return () => clearInterval(id);
	}, []);


	useEffect(() => {

		const fetchWithAutoRefresh = async (url, opts = {}) => {
			// always send cookies + CSRF header
			opts.credentials = 'include';
			opts.headers = {
				...opts.headers,
				'X-CSRF-TOKEN': getCsrfToken()
			};

			let res = await fetch(url, opts);
			if (res.status === 401) {
				// try to refresh the access token
				const refreshRes = await fetch(`/api/token/refresh`, {
					method: 'POST',
					credentials: 'include',
					headers: { 'X-CSRF-TOKEN': getRefreshToken() }
				});
				if (refreshRes.ok) {
					// console.log("Token refreshed...")	
					res = await fetch(url, opts);
				} else {
					setUser(false)
					setCompany(false)
					setAdmin(false)
					throw new Error('Session expired, please log in again');
				}
			}
			return res;
		}

		const loadDevices = async () => {
			// setLoading(true);
			try {
				const res = await fetchWithAutoRefresh(`/api/get-devkits`, {
					method: 'GET'
				});

				if (!res.ok) {
					console.error('Failed to load devkits:', res.status, await res.text());
					return [];
				}

				const { results: allDevices } = await res.json();

				// 2) Enrich each device with `status` and `lastSeenText`
				const enriched = allDevices.map(device => {
					// use the Tailscale last_seen if present, fall back to other fields
					if (device.tailscale.last_seen === null || device.tailscale.connected_to_control) {
						return {
							...device,
							status: 'online',
							lastSeen: '0 minutes ago'
						}
					}
					const rawLastSeen =
						device.tailscale?.last_seen ||
						device.last_seen ||
						device.lastSeen ||
						device.updatedAt ||
						device.createdAt ||
						new Date().toISOString();

					const lastSeenDate = new Date(rawLastSeen);
					const now = new Date();
					const minutesAgo = Math.floor((now - lastSeenDate) / (1000 * 60));

					let status = 'offline';
					let lastSeenText = '';

					if (minutesAgo < 60) {
						lastSeenText = `${minutesAgo} minute${minutesAgo !== 1 ? 's' : ''} ago`;
					} else if (minutesAgo < 1440) {
						const hoursAgo = Math.floor(minutesAgo / 60);
						lastSeenText = `${hoursAgo} hour${hoursAgo !== 1 ? 's' : ''} ago`;
					} else {
						const daysAgo = Math.floor(minutesAgo / 1440);
						lastSeenText = `${daysAgo} day${daysAgo !== 1 ? 's' : ''} ago`;
					}

					return {
						...device,
						status,
						lastSeen: lastSeenText
					};
				});

				// console.log("enriched: ", enriched)

				const uniqueCompanies = Array.from(
					new Set(
						enriched
							.map((d) => d.company?.trim())
							.filter((c) => c && c.length > 0)
					)
				).sort();
				// console.log("uniqueCompaines: ", uniqueCompanies)

				setCompanyList(uniqueCompanies);

				setDevices(prev => {
					const prevById = prev.reduce((acc, d) => {
						acc[d.devkit_id] = d;
						return acc;
					}, {});
					return enriched.map(newDev => {
						const oldDev = prevById[newDev.devkit_id];
						return {
							...newDev,
							versions: oldDev?.versions || {}
						};
					});
				});

				setError(null);
			} catch (err) {
				if (err.message.includes('Session expired')) {
					// redirect to login or clear state
					console.warn(err.message);
					// e.g. history.push('/login');
				} else {
					setError('Failed to load devices. Please try again.');
					console.error('Error loading devices:', err);
				}
			} finally {
				setLoading(false);
			}
		};

		setLoading(true);
		loadDevices();

		const interval = setInterval(loadDevices, 30000);

		return () => clearInterval(interval);
	}, []);


	// Open remote sessions (VNC tunnels / SSH terminals through the backend)
	// plus each devkit's own websocket client count. Polled faster than the
	// device list because a VNC popup opening/closing should show up quickly.
	const [connections, setConnections] = useState(null);
	useEffect(() => {
		let cancelled = false;
		const loadConnections = async () => {
			try {
				const res = await fetchWithRefresh(`/api/connections`, { method: 'GET' });
				if (!res.ok) return;
				const data = await res.json().catch(() => null);
				if (!cancelled && data) setConnections(data);
			} catch (err) {
				if (!String(err?.message || '').includes('Session expired')) {
					console.warn('Failed to load connections:', err);
				}
			}
		};
		loadConnections();
		const id = setInterval(loadConnections, 10000);
		return () => {
			cancelled = true;
			clearInterval(id);
		};
	}, []);

	// Per-devkit view keyed by Tailscale IP, for the cards / list rows.
	const connectionsByHost = useMemo(() => {
		if (!connections) return {};
		const out = {};
		const touch = (ip) => {
			if (!out[ip]) out[ip] = { vnc: 0, ssh: 0, websockets: null, reachable: null };
			return out[ip];
		};
		Object.entries(connections.vnc?.by_host || {}).forEach(([ip, n]) => { touch(ip).vnc = n; });
		Object.entries(connections.ssh?.by_host || {}).forEach(([ip, n]) => { touch(ip).ssh = n; });
		Object.entries(connections.devices || {}).forEach(([ip, d]) => {
			const entry = touch(ip);
			entry.reachable = Boolean(d?.reachable);
			entry.websockets = d?.reachable ? (d.websocket_connections ?? null) : null;
		});
		return out;
	}, [connections]);

	const toggleDevkitSelection = (devkit) => {
		setSelectedDevkits((prev) =>
			prev.find((d) => d.devkit_id === devkit.devkit_id)
				? prev.filter((d) => d.devkit_id !== devkit.devkit_id)
				: [...prev, devkit]
		);
	};

	const clearSelection = () => setSelectedDevkits([]);

	const triggerBatchUpdate = () => {
		// Implement logic to update all selected devkits here
		console.log('Updating:', selectedDevkits);
	};

	const togglePinnedDevkit = (devkitId) => {
		setPinnedDevkits((prev) => {
			const next = prev.includes(devkitId)
				? prev.filter((id) => id !== devkitId)
				: [devkitId, ...prev];
			window.localStorage.setItem(PINNED_DEVKITS_STORAGE_KEY, JSON.stringify(next));
			return next;
		});
	};

	// Devkit ids with an open remote session (VNC/stream/KVM/SSH) in a grid
	// card. In grid view the sessions live INSIDE DevkitCard, so filtering the
	// card out (e.g. "only show online" once the poll flips a device offline)
	// unmounts it and tears the session down — WindowPortal closes the popup
	// mid-session, right after the offline overlay appeared. Cards register
	// here while a session is open and the grid keeps them mounted.
	const [sessionHolders, setSessionHolders] = useState(() => new Set());
	const setDevkitSessionOpen = useCallback((devkitId, open) => {
		setSessionHolders((prev) => {
			if (prev.has(devkitId) === open) return prev;
			const next = new Set(prev);
			if (open) next.add(devkitId);
			else next.delete(devkitId);
			return next;
		});
	}, []);

	const orderedDevices = useMemo(() => {
		if (pinnedDevkits.length === 0) return devices;

		const pinnedSet = new Set(pinnedDevkits);
		const pinned = [];
		const unpinned = [];

		devices.forEach((device) => {
			if (pinnedSet.has(device.devkit_id)) {
				pinned.push(device);
			} else {
				unpinned.push(device);
			}
		});

		pinned.sort(
			(a, b) => pinnedDevkits.indexOf(a.devkit_id) - pinnedDevkits.indexOf(b.devkit_id)
		);

		return [...pinned, ...unpinned];
	}, [devices, pinnedDevkits]);

	// Filter devices based on company selection and status
	const filteredDevices = orderedDevices
		.filter((d) => selectedCompany === null || d.company === selectedCompany)
		.filter((d) => matchesDeviceType(d, selectedDeviceTypes))
		.filter((d) => !onlyShowOnline || d.status === 'online');

	const onlineDevkits = orderedDevices
		.filter((d) => selectedCompany === null || d.company === selectedCompany)
		.filter((d) => matchesDeviceType(d, selectedDeviceTypes))
		.filter((d) => d.status === 'online').length;

	const totalDevkits = orderedDevices
		.filter((d) => selectedCompany === null || d.company === selectedCompany)
		.filter((d) => matchesDeviceType(d, selectedDeviceTypes))
		.length;

	useEffect(() => {
		if (onlineDevkits === 0 && devices.length > 0) {
			checkTailscaleStatus();
		}
		if (onlineDevkits > 0 && tsStatus.indicator !== null) {
			setTsStatus({
				loading: false,
				indicator: null,
				description: null,
				components: [],
				error: null,
				checkedAt: null
			});
		}
	}, [onlineDevkits, devices.length]);

	if (error) {
		return (
			<div className='min-h-screen bg-gray-50 flex items-center justify-center'>
				<div className='text-center'>
					<div className='text-red-600 text-xl mb-4'>{error}</div>
					<button
						onClick={() => window.location.reload()}
						className='bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700'
					>
						Retry
					</button>
				</div>
			</div>
		);
	}

	// A card holding an open remote session is exempt from every filter —
	// hiding it would unmount it and kill the session.
	const filtered = orderedDevices.filter(
		(d) =>
			sessionHolders.has(d.devkit_id) ||
			((!onlyShowOnline || d.status === 'online') &&
				(selectedCompany === null || d.company === selectedCompany) &&
				matchesDeviceType(d, selectedDeviceTypes) &&
				matchesDeviceSearch(d, deviceSearch))
	);

	return (
		<div className="w-full min-h-screen flex flex-col bg-gray-50">
			<DashboardHeader
				admin={admin}
				companyList={companyList}
				selectedCompany={selectedCompany}
				setSelectedCompany={setSelectedCompanyPersisted}
				onlyShowOnline={onlyShowOnline}
				setOnlyShowOnline={setOnlyShowOnlinePersisted}
				selectedDeviceTypes={selectedDeviceTypes}
				setSelectedDeviceTypes={setSelectedDeviceTypesPersisted}
				currentView={currentView}
				setCurrentView={setCurrentViewPersisted}
				onlineDevkits={onlineDevkits}
				totalDevkits={totalDevkits}
				setUser={setUser}
				setAdmin={setAdmin}
				setSidebarOpen={setSidebarOpen}
			/>

			<main className="flex-1 overflow-auto">
				{onlineDevkits === 0 && devices.length > 0 && (
					<div className="px-4 pt-4">
						<div className={[
							"rounded-xl border p-4",
							tsStatus.loading
								? "bg-yellow-50 border-yellow-200"
								: tsStatus.error
									? "bg-red-50 border-red-200"
									: tsStatus.indicator && tsStatus.indicator !== "none"
										? "bg-amber-50 border-amber-200"
										: "bg-blue-50 border-blue-200"
						].join(" ")}>
							<div className="flex items-start justify-between gap-4">
								<div>
									<div className="font-semibold text-gray-900">
										{tsStatus.loading && "Checking Tailscale status…"}
										{!tsStatus.loading && tsStatus.error && "Couldn't reach Tailscale Statuspage"}
										{!tsStatus.loading && !tsStatus.error && tsStatus.indicator === null && "Checking Tailscale status…"}
										{!tsStatus.loading && !tsStatus.error && tsStatus.indicator === 'none' && "Tailscale: All Systems Operational"}
										{!tsStatus.loading && !tsStatus.error && tsStatus.indicator && !['none', 'unknown', null].includes(tsStatus.indicator)
											&& `Tailscale incident: ${tsStatus.description || tsStatus.indicator}`}
										{!tsStatus.loading && !tsStatus.error && tsStatus.indicator === 'unknown' && "Tailscale status unknown"}
									</div>

									{!tsStatus.loading && !tsStatus.error && tsStatus.indicator && tsStatus.indicator !== "none" && (
										<div className="mt-1 text-sm text-gray-700">
											{tsStatus.components
												.filter(c => (c.status || "").toLowerCase() !== "operational")
												.slice(0, 5)
												.map(c => (
													<div key={c.id}>• {c.name}: <span className="uppercase">{c.status}</span></div>
												))
											}
											{tsStatus.components.filter(c => (c.status || "").toLowerCase() !== "operational").length === 0 && (
												<div>No component details available yet.</div>
											)}
										</div>
									)}

									{!tsStatus.loading && !tsStatus.error && tsStatus.indicator === "none" && (
										<div className="mt-1 text-sm text-gray-700">
											Looks like 0 online devices is real (not a provider outage). You can recheck below.
										</div>
									)}

									{!tsStatus.loading && tsStatus.error && (
										<div className="mt-1 text-sm text-gray-700">
											{tsStatus.error}. This could be a local network/CORS hiccup—try again.
										</div>
									)}

									{tsStatus.checkedAt && (
										<div className="mt-1 text-xs text-gray-500">
											Checked {tsStatus.checkedAt.toLocaleTimeString()}
										</div>
									)}
								</div>

								<button
									onClick={() => checkTailscaleStatus(true)}
									className="shrink-0 inline-flex items-center rounded-lg border px-3 py-1.5 text-sm text-gray-700 hover:bg-white bg-gray-50 border-gray-300"
									disabled={tsStatus.loading}
									title="Recheck Tailscale Status"
								>
									{tsStatus.loading ? "Checking…" : "Recheck"}
								</button>
							</div>
						</div>
					</div>
				)}


				<div className="overflow-x-auto">
						<div className="max-w-[85rem] px-4 mx-auto mt-6">
							<BatchUpdateSidebar
								devkits={orderedDevices.filter((d) => d.status === 'online').filter((d) => selectedCompany === null || d.company === selectedCompany).filter((d) => matchesDeviceType(d, selectedDeviceTypes))}
								admin={admin}
								isOpen={sidebarOpen}
								onClose={() => setSidebarOpen(false)}
						/>
					</div>

					{loading ? (
						<div className="flex justify-center items-center py-12 text-gray-500 text-lg">
							Loading Devices...
						</div>
					) : (
						<div className={`${currentView === 'list' ? 'max-w-[95rem]' : 'max-w-[85rem]'} mx-auto mt-6 px-4 mb-12`}>
							{devices.length === 0 ? (
								<div className="text-center py-12">
									<div className="text-gray-500 text-lg">No devices found</div>
								</div>
							) : currentView === 'grid' ? (
								<>
								<div className="mb-4 px-1">
									<DeviceSearchBar
										value={deviceSearch}
										onChange={setDeviceSearch}
										matchCount={filtered.length}
										totalCount={orderedDevices.length}
									/>
								</div>
								<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
									{filtered.length === 0 ? (
										<div className="col-span-1 md:col-span-2 lg:col-span-3">
											<div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-gray-300 bg-white py-14">
												<h3 className="mt-3 text-lg font-semibold text-gray-900">No matching DevKits</h3>
												<p className="mt-1 text-sm text-gray-500">
													{deviceSearch
														? `Nothing matches “${deviceSearch}”.`
														: onlyShowOnline || selectedCompany || selectedDeviceTypes.length > 0
															? "Try adjusting your filters or showing offline devices."
															: "There aren’t any DevKits to display right now."}
												</p>
												<svg className="h-8 w-8 text-gray-400" viewBox="0 0 24 24" fill="none">
													<path d="M4 7h16M4 12h10M4 17h7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
												</svg>
											</div>
										</div>
									) : (
										filtered.map(devkit => (
											<DevkitCard
												key={devkit.tailscale.id}
												devkit={devkit}
												setDevices={setDevices}
												companyList={companyList}
												selectedCompany={selectedCompany}
												isPinned={pinnedDevkits.includes(devkit.devkit_id)}
												onTogglePin={togglePinnedDevkit}
												onSessionOpenChange={setDevkitSessionOpen}
												connections={connectionsByHost[devkit.tailscale?.ipv4?.[0]]}
											/>
										))
									)}
								</div>
								</>
							) : (
								<DevkitListView
									selectedCompany={selectedCompany}
									onlyShowOnline={onlyShowOnline}
									selectedDeviceTypes={selectedDeviceTypes}
									devkits={orderedDevices}
									companyList={companyList}
									setDevices={setDevices}
									pinnedDevkits={pinnedDevkits}
									onTogglePin={togglePinnedDevkit}
									search={deviceSearch}
									onSearchChange={setDeviceSearch}
									connectionsByHost={connectionsByHost}
								/>
							)}
						</div>
					)}

				</div>
			</main>

			<Footer />
		</div>
	);
};

export default Dashboard;
